import { ReactNode } from 'react';
import {
  FileText,
  Image as ImageIcon,
  PenTool,
  Shapes,
  Spline,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { CONTROLLER_ORDER, CONTROLLER_CONFIG } from '@/lib/controllerConfig';
import ControllerLogo from './ControllerLogo';
import carbidecreate from '@/assets/software/carbidecreate.png';
import carveco from '@/assets/software/carveco.png';
import easel from '@/assets/software/easel.png';
import fusion360 from '@/assets/software/fusion360.png';
import vectric from '@/assets/software/vectric.png';
import { PREVIEW_SURFACE, HINT } from './fileCardStyles';

/** The setup diagrams' solids, on the graph paper a file card previews onto: an
 * empty section reads as a sheet waiting to be filled rather than a gap. */
function EmptySheet({
  children,
  caption,
}: {
  children: ReactNode;
  caption: string;
}) {
  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-5',
        PREVIEW_SURFACE
      )}
    >
      {children}
      <p className={HINT}>{caption}</p>
    </div>
  );
}

/** What the list takes, as a huddle of coins. Overlapped rather than spaced:
 * they are one answer to "what goes here", not five things to read. */
function IconCluster({ children }: { children: ReactNode }) {
  return <div className="flex items-center -space-x-2">{children}</div>;
}

function Coin({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border bg-card shadow-sm ring-2 ring-background">
      {children}
    </span>
  );
}

function LogoCoin({ src }: { src: string }) {
  return (
    <Coin>
      <img src={src} alt="" className="h-5 w-5 object-contain" />
    </Coin>
  );
}

/** For a format nobody owns: the tool that draws it, not a vendor mark. */
function GlyphCoin({ Icon, tone }: { Icon: LucideIcon; tone: string }) {
  return (
    <Coin>
      <Icon className={cn('h-5 w-5', tone)} strokeWidth={1.5} />
    </Coin>
  );
}

/** Same three faces at the same three opacities as every solid on Job details,
 * so a form drawn here reads as the same hand. */
function Facets({
  top,
  right,
  left,
}: Record<'top' | 'right' | 'left', string>) {
  return (
    <>
      <polygon points={top} fill="currentColor" opacity={0.25} />
      <polygon points={right} fill="currentColor" opacity={0.45} />
      <polygon points={left} fill="currentColor" opacity={0.6} />
    </>
  );
}

/** One photo in the stack: a plate 6 deep, centred on `cy`. */
function Plate({ cy }: { cy: number }) {
  return (
    <Facets
      top={`80,${cy - 22} 124,${cy} 80,${cy + 22} 36,${cy}`}
      right={`124,${cy} 124,${cy + 6} 80,${cy + 28} 80,${cy + 22}`}
      left={`36,${cy} 36,${cy + 6} 80,${cy + 28} 80,${cy + 22}`}
    />
  );
}

export function NoImagesArt() {
  return (
    <EmptySheet caption="Drop in photos of the finished piece">
      <svg
        viewBox="0 0 160 110"
        aria-hidden
        className="h-[76px] w-[110px] text-muted-foreground"
      >
        <g opacity={0.55}>
          <Plate cy={72} />
          <Plate cy={60} />
        </g>
        <Plate cy={48} />
        {/* The pick, in the accent the Z-zero dot uses for the chosen point. */}
        <circle cx="80" cy="48" r="7" className="fill-primary" />
      </svg>
    </EmptySheet>
  );
}

export function NoGcodeArt() {
  return (
    <EmptySheet caption="Drop in G-code — we sort it by controller">
      <svg
        viewBox="0 0 160 110"
        aria-hidden
        className="h-[76px] w-[110px] text-muted-foreground"
      >
        {/* The same stock the CAD section draws, with the passes cut into it. */}
        <Facets
          top="80,22 124,44 80,66 36,44"
          right="124,44 124,70 80,92 80,66"
          left="36,44 36,70 80,92 80,66"
        />
        <g
          className="stroke-primary"
          fill="none"
          strokeWidth={2}
          strokeLinejoin="round"
        >
          <path d="M80 30 106 44 80 58 54 44Z" />
          <path d="M80 37 93 44 80 51 67 44Z" opacity={0.7} />
          {/* Rapid in, the move that gets the tool to the first pass. */}
          <path d="M80 14v16" strokeDasharray="3 3" opacity={0.55} />
        </g>
      </svg>

      {/* The three columns below already carry their names, so this is the
          same huddle of coins the CAD lists use rather than a second legend. */}
      <IconCluster>
        {CONTROLLER_ORDER.map((type) => (
          <Coin key={type}>
            <ControllerLogo
              name={CONTROLLER_CONFIG[type].name}
              className="h-5 w-5"
            />
          </Coin>
        ))}
      </IconCluster>
    </EmptySheet>
  );
}

/** The three CAD lists share one shell with the sections above them — the same
 * graph paper, the same dashed edge — at a third of the size, because a column
 * is a third as wide. */
function SmallSheet({
  children,
  caption,
}: {
  children: ReactNode;
  caption: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2.5 rounded-lg border border-dashed py-4',
        PREVIEW_SURFACE
      )}
    >
      {children}
      <p className={HINT}>{caption}</p>
    </div>
  );
}

/** The CAM and 3D packages whose own formats this list takes. */
export function NoCadArt() {
  return (
    <SmallSheet caption="CAM projects and 3D models">
      <IconCluster>
        <LogoCoin src={vectric} />
        <LogoCoin src={carveco} />
        <LogoCoin src={fusion360} />
        <LogoCoin src={carbidecreate} />
        <LogoCoin src={easel} />
      </IconCluster>
    </SmallSheet>
  );
}

/** Interchange formats anyone writes, so generic drawing marks rather than a
 * vendor that would be a guess. */
export function NoVectorsArt() {
  return (
    <SmallSheet caption="SVG, DXF and artwork">
      <IconCluster>
        <GlyphCoin Icon={PenTool} tone="text-violet-500" />
        <GlyphCoin Icon={Spline} tone="text-sky-500" />
        <GlyphCoin Icon={Shapes} tone="text-emerald-500" />
      </IconCluster>
    </SmallSheet>
  );
}

export function NoDocumentationArt() {
  return (
    <SmallSheet caption="PDFs and instructions">
      <IconCluster>
        <Coin>
          <FileText className="h-5 w-5 text-rose-500" strokeWidth={1.5} />
        </Coin>
      </IconCluster>
    </SmallSheet>
  );
}

/** Every kind of file the step takes, in one huddle: the controllers whose
 * G-code it reads, the packages whose projects it stores, and the artwork and
 * paperwork that ride along. Shown on the drop zone, which accepts all of it. */
export function AcceptedFormatsCluster() {
  return (
    <IconCluster>
      <Coin>
        <ImageIcon
          className="h-5 w-5 text-muted-foreground"
          strokeWidth={1.5}
        />
      </Coin>
      {CONTROLLER_ORDER.map((type) => (
        <Coin key={type}>
          <ControllerLogo
            name={CONTROLLER_CONFIG[type].name}
            className="h-5 w-5"
          />
        </Coin>
      ))}
      <LogoCoin src={vectric} />
      <LogoCoin src={carveco} />
      <LogoCoin src={fusion360} />
      <LogoCoin src={carbidecreate} />
      <LogoCoin src={easel} />
      <Coin>
        <PenTool className="h-5 w-5 text-violet-500" strokeWidth={1.5} />
      </Coin>
      <Coin>
        <FileText className="h-5 w-5 text-rose-500" strokeWidth={1.5} />
      </Coin>
    </IconCluster>
  );
}
